import { ArticleList } from "@babackapp/customer-ui/components/article";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import {
  useNonReturnableItems,
  useNonReturnableLineItemIds,
} from "@babackapp/return-widget-components/hooks/use-non-returnable-items";
import { hasIncompleteLineItems } from "@babackapp/return-widget-components/lib/draft.lib";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { getOrderCurrency } from "@babackapp/return-widget-components/lib/financials.lib";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useAuth } from "@babackapp/auth-client/hooks";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../../hooks/use-safe-back";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { LineItemRow } from "./line-item-row";
import { NonReturnableRow } from "./non-returnable-row";

export function LineItemSelection() {
  const navigate = useNavigate();
  const { goBack, hasPreviousEntry } = useSafeBack("");
  const t = useCustomText();
  const { orderId, shopSlug, shopId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);
  const allReturnableItems = useReturnableItems(orderId, shopSlug);
  const nonReturnableItems = useNonReturnableItems(orderId, shopSlug);
  const nonReturnableIds = useNonReturnableLineItemIds(orderId, shopSlug);
  const draft = useDraftReturn(orderId);
  const { isAuthenticated } = useAuth();
  const currency = getOrderCurrency(order);
  const connectedUserTooltip = t("misc.connectedUserData");
  const singleArticleOnly = shop?.preferences?.singleArticleReturnOnly === true;
  const hasDraftItems = (draft?.lineItems.length ?? 0) > 0;
  const canContinue = draft != null && hasDraftItems && !hasIncompleteLineItems(draft);
  const returnableLineItems =
    singleArticleOnly && hasDraftItems ? [] : allReturnableItems;

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("articleSelection.title")}</HeaderTitle>
        <HeaderDescription>{t("articleSelection.description")}</HeaderDescription>
      </Header>

      <UserPortalCallout
        texts={shop?.preferences?.userPortal?.articleSelectionMessage}
      />

      <ArticleList className="cursor-pointer max-h-96 overflow-y-auto">
        {returnableLineItems.map((lineItem) => (
          <LineItemRow
            key={lineItem.line_item_id}
            lineItem={lineItem}
            orderId={orderId}
            shopId={shopId}
            currency={currency}
            isConnectedUser={isAuthenticated}
            connectedUserTooltip={connectedUserTooltip}
            isNonReturnable={nonReturnableIds.has(lineItem.line_item_id)}
          />
        ))}
        {nonReturnableItems.map((lineItem) => (
          <NonReturnableRow
            key={lineItem.line_item_id}
            lineItem={lineItem}
            currency={currency}
            isConnectedUser={isAuthenticated}
            connectedUserTooltip={connectedUserTooltip}
          />
        ))}
      </ArticleList>

      <AnimatedButtonRow variants={{}}>
        {hasPreviousEntry && (
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Button variant="secondary" onClick={goBack}>
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </AnimatedButtonContainer>
        )}
        {canContinue && (
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button
              onClick={() =>
                navigate(`/return-widget/${shopId}/order/${orderId}/summary`)
              }
            >
              {t("common.continue")}
            </Button>
          </AnimatedButtonContainer>
        )}
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
